import React from 'react';
import { Typography, Chip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { textToSnakeCase } from '../../utils/utils';
import { designTokens } from '../../theming/design-tokens';

const { colors, spacing, typography } = designTokens;

interface Restaurant {
  physical_address_address_line1?: string;
  physical_address_city?: string;
  'State/Province'?: string;
  physical_address_postalCode?: string;
  Country?: string;
  RestaurantNumber?: number;
  FranchiseGroup?: string;
  RestaurantStatus?: string;
}

interface RestaurantInfoCardProps {
  restaurant: Restaurant;
  className?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  restaurantInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  restaurantId: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.neutral.gray800,
  },
  franchiseGroupText: {
    marginTop: spacing.xs,
    color: colors.neutral.gray600,
  },
  statusChip: {
    color: colors.neutral.white,
    fontWeight: typography.fontWeight.semibold,
  },
  statusChipOpen: {
    backgroundColor: colors.semantic.success,
  },
  statusChipClosed: {
    backgroundColor: colors.semantic.error,
  },
  statusChipOther: {
    backgroundColor: colors.semantic.warning,
  },
}));

const RestaurantInfoCard: React.FC<RestaurantInfoCardProps> = ({
  restaurant,
  className,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!restaurant) {
    return null;
  }

  return (
    <div className={`${classes.restaurantInfo} ${className || ''}`}>
      <div>
        <Typography className={classes.restaurantId}>
          {restaurant.physical_address_address_line1}{' '}
          {restaurant.physical_address_city}, {restaurant['State/Province']}{' '}
          {restaurant.physical_address_postalCode} - {restaurant.Country} #
          {restaurant.RestaurantNumber}{' '}
        </Typography>
        <Typography variant="body2" className={classes.franchiseGroupText}>
          {restaurant.FranchiseGroup}
        </Typography>
      </div>
      <Chip
        label={t(
          `restaurant.status.${textToSnakeCase(restaurant.RestaurantStatus)}`,
          restaurant.RestaurantStatus,
        )}
        size="small"
        className={`${classes.statusChip} ${
          restaurant.RestaurantStatus === 'Open' || !restaurant.RestaurantStatus
            ? classes.statusChipOpen
            : restaurant.RestaurantStatus === 'Closed'
              ? classes.statusChipClosed
              : classes.statusChipOther
        }`}
      />
    </div>
  );
};

export default RestaurantInfoCard;
